import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  LayoutGrid,
  UserRound,
  Wallet,
} from 'lucide-react';
import apiService from '../services/api';
import PlanningRdvDrawer from '../components/planning/PlanningRdvDrawer';
import PlanningMobileList from '../components/planning/PlanningMobileList';
import AujourdHuiDayQueueRows from '../components/aujourdhui/AujourdHuiDayQueueRows';
import { paymentChip } from '../components/planning/PlanningRdvCard';
import { avatarClass, memberInitials, memberRole } from '../components/planning/planningPeople';
import { isOperationalRdv, mapRdvToCommande, RDV_STATUS_UX } from '../utils/equipeRdvRules';
import { getRdvPaymentView, formatEuro } from '../utils/rdvPaymentDisplay';
import { rdvNeedsPaymentFollowUp } from '../utils/rdvPaymentFollowUp';
import {
  clientDisplayName,
  formatParisClock,
  formatParisDayTitle,
  getParisDayApiRange,
  intervenantDisplayName,
  parisYmd,
  rdvDurationMinutes,
} from '../utils/planningDayParis';
import { getOperationalCommercantId } from '../utils/operationalCommercantScope';

function clientContactLine(user) {
  if (!user) return null;
  const tel = String(user.telephone || '').trim();
  if (tel) return tel;
  const email = String(user.email || '').trim();
  if (email) return email;
  return null;
}

function formatDurationShort(minutes) {
  if (!minutes) return '—';
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? '1 h' : `${h} h`;
  }
  return `${minutes} min`;
}

function absenceOverlapsParisDay(absence, ymd) {
  if (!absence?.dateDebut || !absence?.dateFin) return false;
  const dayStart = new Date(`${ymd}T00:00:00`);
  const dayEnd = new Date(`${ymd}T23:59:59`);
  const start = new Date(absence.dateDebut);
  const end = new Date(absence.dateFin);
  return start <= dayEnd && end >= dayStart;
}

function QueueMini({ count, label, tone }) {
  return (
    <div className={`rounded-xl px-2.5 py-2 ${tone}`}>
      <p className="text-lg font-bold tabular-nums leading-none">{count}</p>
      <p className="mt-1 text-[11px] font-medium leading-snug">{label}</p>
    </div>
  );
}

function ProchainRdvCompact({ rdv, onOpen }) {
  if (!rdv) {
    return (
      <section className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-3 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Prochain rendez-vous</h2>
        <p className="mt-2 text-xs text-gray-500">Aucun rendez-vous à venir aujourd&apos;hui.</p>
      </section>
    );
  }

  const statut = String(rdv.statut || '').toUpperCase();
  const statusMeta = RDV_STATUS_UX[statut] || { label: statut || '—', className: 'bg-gray-100 text-gray-700' };
  const pay = paymentChip(rdv);
  const memberOrInst = rdv.instructeur;
  const proName = rdv.instructeurId ? intervenantDisplayName(memberOrInst) : 'Non assigné';
  const proKey = rdv.instructeurId || 'none';
  const contact = clientContactLine(rdv.user);
  const duration = formatDurationShort(rdvDurationMinutes(rdv));

  return (
    <section className="rounded-2xl border border-gray-100 bg-white px-3 py-3 shadow-sm lg:px-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">
        Prochain rendez-vous
      </p>
      <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-0 lg:divide-x lg:divide-gray-100">
        <div className="min-w-[5.5rem] shrink-0 lg:pr-4">
          <p className="text-xl font-bold tabular-nums leading-none text-gray-900 lg:text-2xl">
            {formatParisClock(rdv.dateDebut)}
          </p>
          <p className="mt-0.5 text-[11px] text-gray-500">Aujourd&apos;hui</p>
        </div>

        <div className="min-w-0 flex-1 lg:px-4">
          <p className="truncate text-sm font-semibold text-gray-900">{clientDisplayName(rdv.user)}</p>
          {contact ? <p className="truncate text-[11px] text-gray-500">{contact}</p> : null}
        </div>

        <div className="min-w-0 flex-1 lg:px-4">
          <p className="truncate text-sm font-medium text-gray-800">{rdv.prestation?.nom || 'Prestation'}</p>
          <p className="text-[11px] text-gray-500">{duration}</p>
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-2 lg:px-4">
          <span
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${avatarClass(proKey)}`}
          >
            {proName.slice(0, 2).toUpperCase()}
          </span>
          <span className="min-w-0 truncate text-sm font-medium text-gray-800">{proName}</span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 lg:px-4">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusMeta.className}`}>
            {statusMeta.label}
          </span>
          {pay ? (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${pay.className}`}>
              {pay.label}
            </span>
          ) : null}
        </div>

        <div className="shrink-0 lg:pl-4">
          <button
            type="button"
            onClick={() => onOpen(rdv)}
            className="inline-flex w-full items-center justify-center gap-0.5 rounded-lg bg-violet-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-violet-700 lg:w-auto"
          >
            Ouvrir
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </section>
  );
}

export default function AujourdHui() {
  const todayYmd = parisYmd();
  const [members, setMembers] = useState([]);
  const [absencesToday, setAbsencesToday] = useState(0);
  const [upcomingAbsences, setUpcomingAbsences] = useState([]);
  const [rdvList, setRdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRdv, setSelectedRdv] = useState(null);

  const commercantId = getOperationalCommercantId();

  const loadToday = useCallback(async () => {
    if (!commercantId) {
      setMembers([]);
      setRdvList([]);
      setLoading(false);
      setError('Aucun commerçant associé à ce compte.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { dateMin, dateMax } = getParisDayApiRange(todayYmd);
      const [equipeData, allRdv] = await Promise.all([
        apiService.getEquipe(commercantId),
        apiService.getRendezVousByCommercantAll(commercantId, { dateMin, dateMax }),
      ]);
      setMembers(equipeData.members || []);
      setAbsencesToday(equipeData.absencesToday ?? 0);
      setUpcomingAbsences(equipeData.upcomingAbsences || []);
      setRdvList(allRdv || []);
    } catch (err) {
      setError(err.data?.error?.message || err.message || 'Erreur de chargement');
      setRdvList([]);
    } finally {
      setLoading(false);
    }
  }, [commercantId, todayYmd]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  const professionals = useMemo(
    () => (members || []).filter((m) => m.isIntervenant && m.instructeurId),
    [members],
  );

  const memberByInstructeurId = useMemo(() => {
    const map = {};
    for (const m of professionals) {
      if (m.instructeurId) map[m.instructeurId] = m;
    }
    return map;
  }, [professionals]);

  const dayRdvs = useMemo(
    () =>
      (rdvList || [])
        .filter((rdv) => isOperationalRdv(rdv, { includeHistorical: true }))
        .sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut)),
    [rdvList],
  );

  const ca = useMemo(() => {
    let total = 0;
    let paid = 0;
    let due = 0;
    for (const rdv of dayRdvs) {
      const view = getRdvPaymentView(mapRdvToCommande(rdv));
      if (!view) continue;
      total += Number(view.total) || 0;
      paid += Number(view.amountPaid) || 0;
      due += Number(view.amountDueLater) || 0;
    }
    return { total, paid, due };
  }, [dayRdvs]);

  const queue = useMemo(
    () => ({
      toConfirm: dayRdvs.filter((r) => String(r.statut).toUpperCase() === 'EN_ATTENTE').length,
      payment: dayRdvs.filter(rdvNeedsPaymentFollowUp).length,
      absent: dayRdvs.filter((r) => String(r.statut).toUpperCase() === 'ABSENT').length,
    }),
    [dayRdvs],
  );

  const teamToday = useMemo(() => {
    const absentIds = new Set(
      (upcomingAbsences || [])
        .filter((a) => absenceOverlapsParisDay(a, todayYmd))
        .map((a) => a.instructeurId)
        .filter(Boolean),
    );
    let presents = 0;
    let absents = 0;
    let indisponibles = 0;
    for (const m of professionals) {
      if (!m.intervenantActif) {
        indisponibles += 1;
        continue;
      }
      if (m.instructeurId && absentIds.has(m.instructeurId)) {
        absents += 1;
      } else {
        presents += 1;
      }
    }
    if (absents === 0 && absencesToday > 0) {
      absents = Math.min(absencesToday, professionals.length);
      presents = Math.max(0, professionals.filter((m) => m.intervenantActif).length - absents);
    }
    return { presents, absents, indisponibles };
  }, [professionals, upcomingAbsences, todayYmd, absencesToday]);

  const nextRdv = useMemo(() => {
    const now = Date.now();
    return (
      dayRdvs.find((rdv) => {
        const end = rdv.dateFin ? new Date(rdv.dateFin).getTime() : new Date(rdv.dateDebut).getTime();
        return Number.isFinite(end) && end >= now;
      }) || null
    );
  }, [dayRdvs]);

  const clientsDuJour = useMemo(() => {
    const byUser = new Map();
    for (const rdv of dayRdvs) {
      const key = rdv.user?.id || rdv.userId || clientDisplayName(rdv.user);
      const existing = byUser.get(key) || {
        user: rdv.user,
        userId: rdv.user?.id || rdv.userId,
        name: clientDisplayName(rdv.user),
        count: 0,
        nextIso: null,
      };
      existing.count += 1;
      const iso = rdv.dateDebut;
      if (!existing.nextIso || new Date(iso) < new Date(existing.nextIso)) existing.nextIso = iso;
      byUser.set(key, existing);
    }
    return [...byUser.values()].sort(
      (a, b) => new Date(a.nextIso || 0) - new Date(b.nextIso || 0),
    );
  }, [dayRdvs]);

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-gray-900">Aujourd&apos;hui</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Vue opérationnelle de la journée — lecture rapide et actions sur les rendez-vous.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm">
            <CalendarDays className="h-4 w-4 text-violet-600" />
            {formatParisDayTitle(todayYmd)}
          </span>
          <Link
            to="/planning"
            className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm hover:bg-violet-50"
          >
            <LayoutGrid className="h-4 w-4" />
            Planning
          </Link>
        </div>
      </header>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-3">
        <section className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">À traiter</h2>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <QueueMini
              count={loading ? '—' : queue.toConfirm}
              label="RDV à confirmer"
              tone="bg-amber-50/90 text-amber-950"
            />
            <QueueMini
              count={loading ? '—' : queue.payment}
              label="Paiement à finaliser"
              tone="bg-sky-50/90 text-sky-950"
            />
            <QueueMini
              count={loading ? '—' : queue.absent}
              label="Client absent aujourd'hui"
              tone="bg-rose-50/90 text-rose-950"
            />
            <QueueMini
              count={0}
              label="Anomalie à traiter"
              tone="bg-orange-50/60 text-orange-900/80"
            />
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Équipe aujourd&apos;hui</h2>
            <Link
              to="/equipe"
              className="text-[11px] font-semibold text-violet-700 hover:text-violet-800"
            >
              Voir l&apos;équipe
            </Link>
          </div>
          <dl className="mt-2 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-emerald-50/80 px-2 py-2">
              <dt className="text-[10px] font-medium text-emerald-800">Présents</dt>
              <dd className="text-lg font-bold tabular-nums text-emerald-950">
                {loading ? '—' : teamToday.presents}
              </dd>
            </div>
            <div className="rounded-xl bg-rose-50/80 px-2 py-2">
              <dt className="text-[10px] font-medium text-rose-800">Absents</dt>
              <dd className="text-lg font-bold tabular-nums text-rose-950">
                {loading ? '—' : teamToday.absents}
              </dd>
            </div>
            <div className="rounded-xl bg-slate-50 px-2 py-2">
              <dt className="text-[10px] font-medium text-slate-600">Indisponibles</dt>
              <dd className="text-lg font-bold tabular-nums text-slate-800">
                {loading ? '—' : teamToday.indisponibles}
              </dd>
            </div>
          </dl>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-violet-50/40 p-3 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Chiffre d&apos;affaires du jour</h2>
          <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-violet-800">
            {loading ? '—' : formatEuro(ca.total)}
            <span className="ml-1 text-xs font-semibold text-violet-500">TTC</span>
          </p>
          <div className="mt-2.5 grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-emerald-100/90 bg-emerald-50/95 px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
              <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                <Wallet className="h-3 w-3 opacity-70" />
                Payé
              </p>
              <p className="mt-1 text-sm font-bold tabular-nums leading-none text-emerald-950">
                {loading ? '—' : formatEuro(ca.paid)}
              </p>
            </div>
            <div className="rounded-xl border border-amber-100/90 bg-amber-50/95 px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
              <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                <Clock3 className="h-3 w-3 opacity-70" />
                À encaisser
              </p>
              <p className="mt-1 text-sm font-bold tabular-nums leading-none text-amber-950">
                {loading ? '—' : formatEuro(ca.due)}
              </p>
            </div>
          </div>
        </section>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_288px]">
        <div className="min-w-0 space-y-4">
          <ProchainRdvCompact rdv={nextRdv} onOpen={setSelectedRdv} />

          <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-900">Rendez-vous du jour</h2>
              <span className="text-xs font-medium text-gray-500">
                {dayRdvs.length} rendez-vous
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-gray-500">
              Ordre chronologique — cliquez pour ouvrir le détail.
            </p>
            <div className="mt-3 hidden lg:block">
              <AujourdHuiDayQueueRows
                rdvs={dayRdvs}
                memberByInstructeurId={memberByInstructeurId}
                onRdvClick={setSelectedRdv}
                loading={loading}
              />
            </div>
            <div className="mt-3 lg:hidden">
              <PlanningMobileList
                rdvs={dayRdvs}
                memberByInstructeurId={memberByInstructeurId}
                onRdvClick={setSelectedRdv}
                loading={loading}
                showParticularityIndicator
              />
            </div>
          </section>
        </div>

        <aside className="space-y-3">
          <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Clients du jour</h2>
            <p className="mt-0.5 text-[11px] text-gray-500">
              {loading ? '—' : `${clientsDuJour.length} client${clientsDuJour.length > 1 ? 's' : ''} · ${dayRdvs.length} RDV`}
            </p>
            {loading ? (
              <p className="mt-3 text-xs text-gray-400">Chargement…</p>
            ) : clientsDuJour.length === 0 ? (
              <p className="mt-3 text-xs text-gray-400">Aucun client prévu.</p>
            ) : (
              <ul className="mt-3 max-h-72 space-y-1 overflow-y-auto">
                {clientsDuJour.map((c) => (
                  <li key={c.userId || c.name}>
                    <button
                      type="button"
                      onClick={() => {
                        const rdv = dayRdvs.find(
                          (r) =>
                            (r.user?.id && r.user.id === c.userId) ||
                            clientDisplayName(r.user) === c.name,
                        );
                        if (rdv) setSelectedRdv(rdv);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-1 py-2 text-left hover:bg-violet-50"
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold text-violet-800">
                        {(c.name || '?').slice(0, 2).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-gray-900">
                          {c.name}
                        </span>
                        <span className="block text-[11px] text-gray-500">
                          {c.count > 1 ? `${c.count} RDV` : '1 RDV'}
                          {c.nextIso ? ` · ${formatParisClock(c.nextIso)}` : ''}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Professionnels</h2>
            <ul className="mt-3 space-y-1">
              {professionals.length === 0 ? (
                <li className="text-xs text-gray-400">Aucun intervenant configuré.</li>
              ) : (
                professionals.slice(0, 8).map((m) => (
                  <li key={m.instructeurId} className="flex items-center gap-2 rounded-lg px-1 py-1">
                    <span
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold ${avatarClass(m.instructeurId)}`}
                    >
                      {memberInitials(m)}
                    </span>
                    <span className="min-w-0 truncate text-xs font-medium text-gray-800">
                      {[m.prenom, m.nom].filter(Boolean).join(' ')}
                    </span>
                    <span className="ml-auto truncate text-[10px] text-gray-400">{memberRole(m)}</span>
                  </li>
                ))
              )}
            </ul>
            {teamToday.absents > 0 ? (
              <p className="mt-2 flex items-center gap-1 text-[11px] font-medium text-rose-700">
                <UserRound className="h-3.5 w-3.5" />
                {teamToday.absents} absent{teamToday.absents > 1 ? 's' : ''} aujourd&apos;hui
              </p>
            ) : null}
          </section>
        </aside>
      </div>

      {selectedRdv ? (
        <PlanningRdvDrawer
          rdv={selectedRdv}
          member={
            (members || []).find(
              (m) => m.instructeurId && m.instructeurId === selectedRdv.instructeurId,
            ) || null
          }
          commercantId={commercantId}
          onClose={() => setSelectedRdv(null)}
          onUpdated={(next) => {
            setSelectedRdv(next);
            loadToday();
          }}
        />
      ) : null}
    </div>
  );
}
